import {
  useSalesServiceSalesInvoicesBrokerPayCreate,
  useSalesServiceSalesInvoicesSendPaymentLinkCreate,
  useSalesServiceSalesInvoicesStaffPayCreate,
  useSalesServiceSalesOrdersCheckoutCreate,
  useSalesServiceSalesOrdersPartialUpdate
} from '@insurance/api/queries'
import {
  InvoiceDetail,
  OrderEditStatusEnum,
  PatchedOrderEdit,
  PaymentTypeStaff
} from '@insurance/api/requests'
import {
  PayBrokerButton,
  PaymentExtendType,
  PaymentValue,
  PayStaffButton
} from '@insurance/components'
import { useAppNavigate } from '@insurance/hooks'
import { paths } from '@insurance/routes'
import { useSessionContext } from '@shared/hooks'
import {
  getFormatCurrency,
  replaceEmptyWithUndefined,
  showApiErrors
} from '@shared/utils'
import { App, Button, Flex, Form, Modal } from 'antd'
import { useTranslation } from 'react-i18next'

const { confirm } = Modal

const stepKey = { name: 'step' }
const priceKey = { name: 'price' }
const orderKey = { name: 'order_id' }
const vehicleDataProps = { name: 'vehicle_data' }

type Props = {
  steps: number
  onReadyToSaveData?: () => void
  removeFromStorage: () => void
  handleCalculate: (onSuccess: () => void) => Promise<void>
  isCalculatingLoading: boolean
  normalizeOrderData: (values: Record<string, unknown>) => {
    id: number
    requestBody: PatchedOrderEdit
  }
  disabledPay?: boolean
  errorUnwrapKeys?: string[]
}

export const InsuranceRcaControls = ({
  steps,
  onReadyToSaveData,
  removeFromStorage,
  handleCalculate,
  isCalculatingLoading,
  normalizeOrderData,
  disabledPay,
  errorUnwrapKeys
}: Props) => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const { message } = App.useApp()
  const { broker } = useSessionContext()
  const navigate = useAppNavigate()

  const step = Form.useWatch(stepKey.name, { preserve: true })
  const price = Form.useWatch(priceKey.name, { preserve: true })

  const hasPrevStep = step > 0
  const isCalculateStep = step === 0
  const isPayStep = step === steps - 1

  const handleFinishStepError = (error: unknown) => {
    showApiErrors(error, formInstance, { unwrapKeys: errorUnwrapKeys })
  }

  const handleChangeStep = (newStep: number) => {
    formInstance.setFieldValue(stepKey.name, newStep)
    onReadyToSaveData?.()
  }

  const handlePrevStep = () => {
    const prevStep = step - 1
    handleChangeStep(prevStep)
    if (!prevStep) {
      formInstance.setFieldValue(priceKey.name, undefined)
      formInstance.setFieldValue(orderKey.name, undefined)
      formInstance.setFieldValue(vehicleDataProps.name, undefined)
    }
  }

  const handleCalculateClick = () => {
    handleCalculate(() => handleChangeStep(step + 1))
  }

  const { mutate: payBroker, isPending: isBrokerPayLoading } =
    useSalesServiceSalesInvoicesBrokerPayCreate({
      onSuccess: ({ invoice }) => {
        removeFromStorage()
        message.success(t('insurance_created_successfully'))
        navigate(
          invoice?.order.id
            ? paths.orders.details(invoice.order.id)
            : paths.salesPanel.root
        )
      },
      onError: handleFinishStepError
    })

  const { mutate: pay, isPending: isPayLoading } =
    useSalesServiceSalesInvoicesStaffPayCreate({
      onSuccess: ({ invoice }) => {
        removeFromStorage()
        message.success(t('insurance_created_successfully'))
        navigate(
          invoice?.order.id
            ? paths.orders.details(invoice.order.id)
            : paths.salesPanel.root
        )
      },
      onError: (error, variables) => {
        handleFinishStepError(error)

        const isPayCash =
          variables?.requestBody?.payment_type === PaymentTypeStaff.CASH
        const errorStatus = (error as { status?: number })?.status

        if (isPayCash && errorStatus !== 500) {
          confirm({
            title: t('error_payment'),
            content: t('error_payment_confirmation'),
            okText: t('pay_manually'),
            onOk: () => {
              pay({
                id: variables.id,
                requestBody: { ...variables.requestBody, force_manual: true }
              })
            }
          })
        }
      }
    })

  const { mutate: checkoutOrder, isPending: isCheckoutLoading } =
    useSalesServiceSalesOrdersCheckoutCreate({
      onError: handleFinishStepError
    })

  const { mutate: paymentLink, isPending: isPaymentLinkLoading } =
    useSalesServiceSalesInvoicesSendPaymentLinkCreate({
      onSuccess: () => {
        removeFromStorage()
        message.success(t('insurance_created_successfully'))
      },
      onError: handleFinishStepError
    })

  const { mutate: updateOrder, isPending: isInitOrderLoading } =
    useSalesServiceSalesOrdersPartialUpdate({
      onError: handleFinishStepError
    })

  const handleInitBrokerOrder = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    const handleSuccess = (invoice: InvoiceDetail | null) => {
      if (!invoice?.id) return
      payBroker({ id: invoice.id })
    }

    const requestData = replaceEmptyWithUndefined(normalizeOrderData(values))

    updateOrder(
      {
        ...requestData,
        requestBody: {
          ...requestData.requestBody,
          status: OrderEditStatusEnum.DATA_ENTRY,
          stage: 0
        }
      },
      {
        onSuccess: ({ id }) =>
          checkoutOrder(
            { id },
            { onSuccess: handleSuccess, onError: handleFinishStepError }
          )
      }
    )
  }

  const handleInitStaffOrder = async (paymentValue: PaymentValue) => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    const handleSuccess = (invoice: InvoiceDetail | null) => {
      if (!invoice?.id) return

      if (paymentValue.paymentType !== PaymentExtendType.ONLINE_PAYMENT)
        pay({
          id: invoice.id,
          requestBody: {
            payment_type: paymentValue.paymentType,
            cash_box: paymentValue.cashBox
          }
        })

      if (paymentValue.paymentType === PaymentExtendType.ONLINE_PAYMENT)
        paymentLink(
          { id: invoice.id },
          {
            onSuccess: () => {
              navigate(
                invoice.order.id
                  ? paths.orders.details(invoice.order.id)
                  : paths.salesPanel.root
              )
            }
          }
        )
    }

    const requestData = replaceEmptyWithUndefined(normalizeOrderData(values))

    updateOrder(
      {
        ...requestData,
        requestBody: {
          ...requestData.requestBody,
          status: OrderEditStatusEnum.DATA_ENTRY,
          stage: 0
        }
      },
      {
        onSuccess: ({ id }) =>
          checkoutOrder(
            {
              id,
              requestBody: { credit: replaceEmptyWithUndefined(values.credit) }
            },
            { onSuccess: handleSuccess }
          )
      }
    )
  }

  return (
    <Flex justify="end" gap={12}>
      {hasPrevStep && (
        <Button color="primary" variant="outlined" onClick={handlePrevStep}>
          {t('prev_step')}
        </Button>
      )}

      {isCalculateStep && (
        <Button
          type="primary"
          loading={isCalculatingLoading}
          onClick={handleCalculateClick}
        >
          {t('calculate')}
        </Button>
      )}

      {isPayStep && !broker && (
        <PayStaffButton
          price={getFormatCurrency({ amount: price?.amount })}
          loading={
            isInitOrderLoading ||
            isPayLoading ||
            isPaymentLinkLoading ||
            isCheckoutLoading
          }
          disabled={disabledPay}
          onPay={handleInitStaffOrder}
        />
      )}

      {isPayStep && broker && (
        <PayBrokerButton
          loading={
            isInitOrderLoading || isBrokerPayLoading || isCheckoutLoading
          }
          disabledPay={disabledPay}
          onPay={handleInitBrokerOrder}
        />
      )}
    </Flex>
  )
}
